import { api } from '../../../lib/api';

// Types

export interface Integration {
  id: string;
  storeId: string;
  name: string;
  type: 'native' | 'openapi' | 'knowledge_only';
  platform: string | null;
  baseUrl: string | null;
  authType: string | null;
  hasCredentials: boolean;
  active: boolean;
  healthStatus: string;
  lastSyncedAt: string | null;
  integrationMetadata: Record<string, unknown>;
  endpointCount: number;
  createdAt: string;
  updatedAt: string;
  endpoints?: IntegrationEndpoint[];
}

export interface IntegrationEndpoint {
  id: string;
  integrationId: string;
  path: string;
  method: string;
  operationId: string | null;
  toolName: string;
  toolDescription: string;
  parameters: any;
  requestBodySchema: any;
  responseSchema: any;
  parameterMapping: Record<string, string> | null;
  permissionLevel: 'read' | 'write' | 'admin';
  requiresApproval: boolean;
  enabled: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface ParsedEndpoint {
  path: string;
  method: string;
  operationId: string | null;
  summary: string | null;
  description: string | null;
  tags: string[];
  parameters: any[];
  requestBody: any;
  responseSchema: any;
  suggestedToolName: string;
  suggestedDescription: string;
  suggestedPermission: string;
}

export interface SpecSummary {
  title: string | null;
  description: string | null;
  version: string;
  baseUrl: string | null;
  endpointCount: number;
  tags: string[];
  methods: Record<string, number>;
}

export interface IntegrationStats {
  integrationId: string;
  period: string;
  totalCalls: number;
  successfulCalls: number;
  failedCalls: number;
  successRate: number;
  avgDurationMs: number;
  maxDurationMs: number;
  minDurationMs: number;
  healthStatus: string;
  errorBreakdown: Record<string, number>;
}

export interface IntegrationLog {
  id: string;
  toolName: string;
  method: string;
  url: string;
  statusCode: number | null;
  success: boolean;
  durationMs: number;
  errorMessage: string | null;
  permissionLevel: string;
  requiredApproval: boolean;
  createdAt: string;
}

export interface BusinessType {
  type: string;
  label: string;
  categories: { value: string; label: string }[];
}

// Service

export const integrationService = {
  // Integration CRUD
  async list(): Promise<Integration[]> {
    return api.get<Integration[]>('/integrations');
  },

  async get(id: string): Promise<Integration> {
    return api.get<Integration>(`/integrations/${id}`);
  },

  async create(data: {
    name: string;
    type: string;
    platform?: string;
    base_url?: string;
    auth_type?: string;
    auth_config?: Record<string, string>;
    integration_metadata?: Record<string, unknown>;
  }): Promise<Integration> {
    return api.post<Integration>('/integrations', data);
  },

  async update(id: string, data: Record<string, unknown>): Promise<Integration> {
    return api.put<Integration>(`/integrations/${id}`, data);
  },

  async remove(id: string): Promise<void> {
    await api.delete(`/integrations/${id}`);
  },

  // OpenAPI
  async parseSpec(data: { spec_text?: string; spec_url?: string }): Promise<{
    summary: SpecSummary;
    endpoints: ParsedEndpoint[];
  }> {
    return api.post('/integrations/openapi/parse', data);
  },

  async saveEndpoints(integrationId: string, endpoints: any[]): Promise<{ saved: number }> {
    return api.post(`/integrations/${integrationId}/openapi/save-endpoints`, { endpoints });
  },

  async refreshSpec(integrationId: string): Promise<{
    summary: SpecSummary;
    endpoints: ParsedEndpoint[];
  }> {
    return api.post(`/integrations/${integrationId}/openapi/refresh-spec`, {});
  },

  // Health & Testing
  async testConnection(id: string): Promise<{ reachable: boolean; statusCode?: number; latencyMs?: number; error?: string }> {
    return api.post(`/integrations/${id}/test`, {});
  },

  async getHealth(id: string): Promise<{ integrationId: string; status: string }> {
    return api.get(`/integrations/${id}/health`);
  },

  // Logs & Stats
  async getLogs(id: string, params?: { limit?: number; offset?: number; success_only?: boolean }): Promise<{
    total: number;
    logs: IntegrationLog[];
  }> {
    const qs = new URLSearchParams();
    if (params?.limit) qs.set('limit', String(params.limit));
    if (params?.offset) qs.set('offset', String(params.offset));
    if (params?.success_only !== undefined) qs.set('success_only', String(params.success_only));
    const query = qs.toString();
    return api.get(`/integrations/${id}/logs${query ? `?${query}` : ''}`);
  },

  async getStats(id: string, days?: number): Promise<IntegrationStats> {
    const qs = days ? `?days=${days}` : '';
    return api.get(`/integrations/${id}/stats${qs}`);
  },

  // Endpoints
  async getEndpoints(id: string): Promise<IntegrationEndpoint[]> {
    return api.get(`/integrations/${id}/endpoints`);
  },

  async updateEndpoint(integrationId: string, endpointId: string, data: Record<string, unknown>): Promise<IntegrationEndpoint> {
    return api.put(`/integrations/${integrationId}/endpoints/${endpointId}`, data);
  },

  // Business types
  async getBusinessTypes(): Promise<BusinessType[]> {
    return api.get('/stores/business-types');
  },

  async getCategories(): Promise<{ businessType: string; categories: { value: string; label: string }[] }> {
    return api.get('/stores/categories');
  },
};
